import { useCategories } from "@/api/cms";
import { useSaneModifiableList } from "@/api/modifiableList";
import { constructLocaleDict, getMainLanguage, SupportedLocale } from "@/i18n";
import {
  CatalogueRaisonneSection,
  CatalogueRaisonneSectionUpsert,
} from "@/lib/cms/cms-types";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { generateImageName } from "@/lib/utils";
import { ChevronDown, ChevronUp, GripVertical, Trash2 } from "lucide-react";
import { useGetAttachmentById } from "@/api/attachment";
import useMediaAttachment from "@/hooks/useMediaAttachment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Path, useFieldArray, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";
import { MediaPreview } from "../media/MediaPreview";
import * as AF from "../form-items/AbstractForm";
import { ModifiableList } from "../form-items/ModifiableList";
import { MultilingualField } from "../form-items/MultilingualField";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { Sortable, SortableDragHandle, SortableItem } from "../ui/sortable";

const SORT_VALUES_EXCLUDED_FOR_MULTIPRINTS: string[] = [
  "categorie:asc",
  "categorie:desc",
  "serie:asc",
  "serie:desc",
  "themes:asc",
  "themes:desc",
];

// Les multiples n'ont ni catégorie, ni série, ni thème sur leur fiche.
const DECOUPAGE_TYPES_EXCLUDED_FOR_MULTIPRINTS: string[] = [
  "categories",
  "series",
  "themes",
];

const TypeOeuvreTextField = ({
  name,
  label,
  lang,
  categoryId,
  form,
  tooltipText,
}: {
  name: string;
  label: string;
  lang: SupportedLocale;
  categoryId: string | undefined;
  form: UseFormReturn<any>;
  tooltipText?: string;
}) => {
  const mainLang = getMainLanguage();
  const { data: mainLangData } = useSaneModifiableList(
    "liste-types-oeuvres",
    mainLang,
  );

  const matchingOrder = useMemo(() => {
    if (!categoryId || !mainLangData) return null;
    const match = mainLangData.find((item) => item.value === categoryId);
    return match?.order ?? null;
  }, [categoryId, mainLangData]);

  const prefillValue = useMemo(() => {
    if (matchingOrder === null) return "";
    return (
      mainLangData?.find((item) => item.order === matchingOrder)?.value ?? ""
    );
  }, [matchingOrder, mainLangData]);

  useEffect(() => {
    if (prefillValue && categoryId) {
      form.setValue(name, prefillValue, { shouldDirty: false });
    }
  }, [prefillValue, categoryId, name, form]);

  return (
    <ModifiableList
      listKey="liste-types-oeuvres"
      name={name}
      label={label}
      placeholder={label}
      lang={lang}
      tooltipText={tooltipText}
    />
  );
};

type DecoupageFormValues = {
  decoupages?: CatalogueRaisonneSectionUpsert[];
  type?: string | undefined;
  target?: "ARTWORKS" | "MULTIPRINTS" | undefined;
  sortByInsideDecoupage?: string;
};

type DecoupageCardProps = {
  index: number;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onRemove: () => void;
  form: UseFormReturn<DecoupageFormValues>;
  target: "ARTWORKS" | "MULTIPRINTS";
  cmsID: string;
  update: (index: number, value: CatalogueRaisonneSectionUpsert) => void;
  showCategoryFields: boolean;
  selectedType: string | undefined;
  categoriesData: { value: string }[] | undefined;
  isLoadingCategories: boolean;
  categoryLabel: string;
};

const DecoupageCard = ({
  index,
  isCollapsed,
  onToggleCollapse,
  onRemove,
  form,
  target,
  cmsID,
  update,
  showCategoryFields,
  selectedType,
  categoriesData,
  isLoadingCategories,
  categoryLabel,
}: DecoupageCardProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const isMultiprints = target === "MULTIPRINTS";
  const typeTooltip = isMultiprints
    ? vt(
        "bo.cms.decoupage.typeMultiprintTooltip",
        "Indiquez ici le type d'estampe / multiple dans les différentes langues",
      )
    : t("typeOeuvreTooltip");
  const categoryPlaceholder =
    isMultiprints && selectedType === "type"
      ? vt(
          "bo.cms.decoupage.selectTypeMultiprint",
          "Sélectionner un type d'estampe / multiple",
        )
      : t(
          "select" +
            selectedType?.charAt(0).toUpperCase() +
            selectedType?.slice(1),
        );
  const { watch } = form;
  const mainLang = getMainLanguage();
  const imagePosition = `decoupage${target}-${index}`;
  const formVignette = watch(`decoupages.${index}.vignette`);

  const {
    attachment: polledByPosition,
    previewImageUrl: polledPreviewUrl,
    refetch: refetchByPosition,
  } = useMediaAttachment(imagePosition, cmsID);

  const { data: polledById, refetch: refetchById } = useGetAttachmentById(
    formVignette?.id ?? "",
    { pollWhileProcessing: true },
  );

  const resolvedAttachment =
    polledById ?? polledByPosition ?? formVignette ?? undefined;
  const resolvedPreviewUrl =
    resolvedAttachment?.mediumPublicUrl ??
    resolvedAttachment?.posterPublicUrl ??
    polledPreviewUrl;

  const refetchVignette = useCallback(() => {
    void refetchByPosition();
    if (formVignette?.id) void refetchById();
  }, [refetchByPosition, refetchById, formVignette?.id]);

  useEffect(() => {
    if (!polledByPosition?.id && !polledById?.id) return;

    const latest = polledById ?? polledByPosition;
    if (!latest?.id) return;

    const current = form.getValues(`decoupages.${index}.vignette`);
    if (current?.id !== latest.id) return;

    // Ne PAS comparer les URLs signées (medium/poster/video) : chaque refetch
    // régénère la signature → update() → remount → refetch → boucle infinie
    // (vignettes qui clignotent). On ne sync que les changements structurels
    // (ex. vidéo processing → ready).
    const hasStructuralMediaChange =
      latest.kind !== current?.kind ||
      latest.videoStatus !== current?.videoStatus ||
      (!!latest.videoPublicUrl && !current?.videoPublicUrl) ||
      (!!latest.posterPublicUrl &&
        !current?.posterPublicUrl &&
        latest.kind === "video");

    if (hasStructuralMediaChange) {
      update(index, {
        ...form.getValues(`decoupages.${index}`),
        vignette: {
          ...latest,
          applyWatermark: latest.applyWatermark ?? false,
        },
      });
    }
  }, [polledByPosition, polledById, form, index, update]);

  const sectionNamePath =
    `decoupages.${index}.nameMultilingual.${mainLang}` as Path<DecoupageFormValues>;
  const sectionNamePreview =
    (watch(sectionNamePath) as string | undefined) ?? "";

  return (
    <div className="rounded-lg border p-4">
      <div className={`flex items-center gap-2 ${!isCollapsed ? "mb-4" : ""}`}>
        <SortableDragHandle
          type="button"
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 mt-0.5"
          aria-label={t("catalogueRaisonne.decoupage.reorder")}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </SortableDragHandle>

        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold">
            {t("decoupage")} {index + 1}
            {isCollapsed && sectionNamePreview ? (
              <span className="ml-2 text-sm font-normal text-muted-foreground truncate">
                — {sectionNamePreview}
              </span>
            ) : null}
          </h3>
        </div>

        <div className="flex items-center shrink-0 gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="bg-transparent hover:bg-transparent"
            onClick={onToggleCollapse}
            aria-label={
              isCollapsed
                ? t("catalogueRaisonne.decoupage.expand")
                : t("catalogueRaisonne.decoupage.collapse")
            }
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="bg-transparent hover:bg-transparent"
            onClick={onRemove}
            aria-label={t("delete")}
          >
            <Trash2 className="h-5 w-5 text-red-500 hover:text-red-900 transition-colors" />
          </Button>
        </div>
      </div>

      {!isCollapsed && (
        <>
          {showCategoryFields ? (
            <AF.SelectField
              label={""}
              name={`decoupages.${index}.categoryId`}
              options={
                categoriesData?.map((category) => ({
                  value: category.value,
                  label: category.value,
                })) ?? []
              }
              placeholder={categoryPlaceholder}
              disabled={isLoadingCategories}
              className="mb-4"
            />
          ) : (
            <AF.Row className="mb-4">
              <AF.Text
                name={`decoupages.${index}.startValue`}
                label={
                  selectedType === "creationDate"
                    ? t("startYear")
                    : t("startNumber")
                }
                placeholder={
                  selectedType === "creationDate"
                    ? t("startYear")
                    : t("startNumber")
                }
              />
              <AF.Text
                name={`decoupages.${index}.endValue`}
                label={
                  selectedType === "creationDate"
                    ? t("endYear")
                    : t("endNumber")
                }
                placeholder={
                  selectedType === "creationDate"
                    ? t("endYear")
                    : t("endNumber")
                }
              />
            </AF.Row>
          )}
          <AF.Checkbox
            name={`decoupages.${index}.showContent`}
            field={{
              defaultValue: watch(`decoupages.${index}.showContent`) ?? false,
            }}
            label={t("catalogueRaisonne.decoupage.showContent")}
            className="mb-4"
          />
          <MultilingualField
            fixed={getMainLanguage()}
            render={(lang) => {
              const categoryId =
                watch(`decoupages.${index}.categoryId`) ?? undefined;
              const showContent =
                watch(`decoupages.${index}.showContent`) ?? false;
              return (
                <div className="flex flex-col gap-2">
                  {selectedType === "type" && (
                    <TypeOeuvreTextField
                      name={`decoupages.${index}.categoryMultilingual.${lang}`}
                      label={categoryLabel}
                      lang={lang}
                      categoryId={categoryId}
                      form={form}
                      tooltipText={typeTooltip}
                    />
                  )}
                  <AF.Text
                    name={`decoupages.${index}.nameMultilingual.${lang}`}
                    label={t("sectionName")}
                    placeholder={t("sectionName")}
                  />
                  {showContent && (
                    <div className="flex flex-col gap-2">
                      <AF.Text
                        name={`decoupages.${index}.titleMultilingual.${lang}`}
                        label={t("titleSection")}
                        placeholder={t("titleSection")}
                      />
                      <AF.RichText
                        name={`decoupages.${index}.contentMultilingual.${lang}`}
                        label={t(
                          "catalogueRaisonne.decoupage.presentationText",
                        )}
                      />
                    </div>
                  )}
                </div>
              );
            }}
          />
          <MultilingualField
            className="mt-4"
            excludeLang={getMainLanguage()}
            render={(lang) => {
              const showContent =
                watch(`decoupages.${index}.showContent`) ?? false;
              return (
                <div className="flex flex-col gap-2">
                  {selectedType === "type" && (
                    <ModifiableList
                      listKey="liste-types-oeuvres"
                      name={`decoupages.${index}.categoryMultilingual.${lang}`}
                      label={categoryLabel}
                      placeholder={categoryLabel}
                      lang={lang}
                      tooltipText={typeTooltip}
                    />
                  )}
                  <AF.Text
                    name={`decoupages.${index}.nameMultilingual.${lang}`}
                    label={t("sectionName")}
                    placeholder={t("sectionName")}
                  />
                  {showContent && (
                    <div className="flex flex-col gap-2">
                      <AF.Text
                        name={`decoupages.${index}.titleMultilingual.${lang}`}
                        label={t("titleSection")}
                        placeholder={t("titleSection")}
                      />
                      <AF.RichText
                        name={`decoupages.${index}.contentMultilingual.${lang}`}
                        label={t(
                          "catalogueRaisonne.decoupage.presentationText",
                        )}
                      />
                    </div>
                  )}
                </div>
              );
            }}
          />

          <Separator className="my-6" />

          <AF.Col className="mt-4">
            <p className="block text-sm font-medium mb-2">
              {t("vignetteImage")}
            </p>
            <div className="flex gap-2 items-stretch w-full max-w-lg">
              <CroppedImageInput
                inlineActions
                buttonSize="sm"
                attachment={resolvedAttachment}
                imageURL={resolvedPreviewUrl}
                refetch={refetchVignette}
                imagePosition={imagePosition}
                docId={cmsID}
                aspect={1.5}
                forceCreateNewAttachment={true}
                buildImageName={(instanceName) =>
                  generateImageName({
                    primaryType: "thumbnail",
                    instanceName,
                    pageNameInDefaultLanguage: "CatalogueRaisonne",
                    title: `decoupage-${index}`,
                  })
                }
                onSuccess={(attachment) => {
                  if (!attachment.id) return;

                  const currentValues = form.getValues(`decoupages.${index}`);

                  update(index, {
                    ...currentValues,
                    vignette: {
                      ...attachment,
                      id: attachment.id,
                      applyWatermark: attachment.applyWatermark ?? false,
                      photoCredits: attachment.photoCredits ?? "",
                    },
                  });
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 min-w-0 font-semibold"
                disabled={!resolvedAttachment}
                onClick={() => {
                  const currentValues = form.getValues(`decoupages.${index}`);
                  update(index, {
                    ...currentValues,
                    vignette: null,
                  });
                }}
              >
                {t("removePhoto")}
              </Button>
            </div>
            <MediaPreview
              attachment={resolvedAttachment}
              previewImageUrl={resolvedPreviewUrl}
            />
          </AF.Col>
        </>
      )}
    </div>
  );
};

const DecoupageCatalogueRaisonneForm = ({
  form,
  target,
  sections,
  cmsID,
}: {
  form: UseFormReturn<DecoupageFormValues>;
  target: "ARTWORKS" | "MULTIPRINTS";
  sections: CatalogueRaisonneSection[];
  cmsID: string;
}) => {
  const { t } = useTranslation();
  const vt = useVisibleT();

  const { control, watch } = form;

  const selectableCategoryTypes = ["categories", "series", "themes", "type"];

  const selectedType = watch("type");
  const showCategoryFields = selectableCategoryTypes.includes(
    selectedType as string,
  );
  const categoryLabel =
    target === "MULTIPRINTS"
      ? vt(
          "bo.cms.decoupage.typeMultiprintForSection",
          "Type d'estampe / multiple pour cette section",
        )
      : t("categoryLabel", {
          defaultValue: t("categoryLabel"),
        });
  const selectTypePlaceholder =
    target === "MULTIPRINTS"
      ? vt(
          "bo.cms.decoupage.selectTypeMultiprint",
          "Sélectionner un type d'estampe / multiple",
        )
      : t("selectType");

  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategories(selectedType);

  const { fields, append, remove, update, replace, move } = useFieldArray({
    control,
    name: "decoupages",
  });

  const [collapsedById, setCollapsedById] = useState<Record<string, boolean>>(
    {},
  );

  const toggleCollapsed = useCallback((fieldId: string) => {
    setCollapsedById((prev) => ({
      ...prev,
      [fieldId]: !prev[fieldId],
    }));
  }, []);

  const handleMove = useCallback(
    ({
      activeIndex,
      overIndex,
    }: {
      activeIndex: number;
      overIndex: number;
    }) => {
      move(activeIndex, overIndex);
      const decoupages = form.getValues("decoupages");
      if (decoupages) {
        decoupages.forEach((_, i) => {
          form.setValue(`decoupages.${i}.order`, i + 1, { shouldDirty: true });
        });
      }
    },
    [move, form],
  );

  // Empreinte stable (ids) : évite replace() à chaque refetch de `sections`
  // (référence tableau neuve + vignettes re-signées) qui remontait toutes les cartes.
  const sectionsFingerprint = useMemo(
    () =>
      sections
        .map((s) => `${s.id ?? ""}:${s.type ?? ""}:${s.order ?? ""}`)
        .join("|"),
    [sections],
  );

  useEffect(() => {
    if (!selectedType) return;

    const filtered = sections
      .filter((s) => s.type === selectedType)
      .map((section) => ({
        ...section,
        categoryMultilingual:
          section.categoryMultilingual ?? constructLocaleDict(() => ""),
      }));

    replace(filtered);
    let sortBy = filtered[0]?.sortByInsideDecoupage ?? "dateCreation:asc";
    if (
      target === "MULTIPRINTS" &&
      SORT_VALUES_EXCLUDED_FOR_MULTIPRINTS.includes(sortBy)
    ) {
      sortBy = "dateCreation:asc";
    }
    form.setValue("sortByInsideDecoupage", sortBy, { shouldDirty: false });
    // sections lu via fingerprint ; pas `sections` en deps (identité instable).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sectionsFingerprint
  }, [selectedType, sectionsFingerprint, replace, form, target]);

  const typeOptions = [
    { value: "creationDate", label: t("creationDate") },
    { value: "categories", label: t("oeuvresDescriptionCategorie") },
    { value: "series", label: t("oeuvresDescriptionSerie") },
    { value: "themes", label: t("oeuvresDescriptionTheme") },
    { value: "registrationNumber", label: t("registrationNumber") },
    {
      value: "type",
      label:
        target === "MULTIPRINTS"
          ? t("estampesMultiplesDescriptionType")
          : t("typeOeuvre"),
    },
  ].filter(
    (opt) =>
      target !== "MULTIPRINTS" ||
      !DECOUPAGE_TYPES_EXCLUDED_FOR_MULTIPRINTS.includes(opt.value),
  );

  const sortOptions = useMemo(() => {
    const all = [
      { value: "dateCreation:asc", label: t("sortByDateCreationAsc") },
      { value: "dateCreation:desc", label: t("sortByDateCreationDesc") },
      { value: "title:asc", label: t("sortByTitleAsc") },
      { value: "title:desc", label: t("sortByTitleDesc") },
      { value: "numeroEnregistrement:asc", label: t("sortByNumeroAsc") },
      { value: "numeroEnregistrement:desc", label: t("sortByNumeroDesc") },
      { value: "categorie:asc", label: t("sortByCategorieAsc") },
      { value: "categorie:desc", label: t("sortByCategorieDesc") },
      { value: "serie:asc", label: t("sortBySerieAsc") },
      { value: "serie:desc", label: t("sortBySerieDesc") },
      { value: "themes:asc", label: t("sortByThemesAsc") },
      { value: "themes:desc", label: t("sortByThemesDesc") },
      { value: "workType:asc", label: t("sortByWorkTypeAsc") },
      { value: "workType:desc", label: t("sortByWorkTypeDesc") },
      { value: "createdAt:asc", label: t("sortByCreatedAtAsc") },
      { value: "createdAt:desc", label: t("sortByCreatedAtDesc") },
    ];
    if (target === "MULTIPRINTS") {
      return all.filter(
        (opt) => !SORT_VALUES_EXCLUDED_FOR_MULTIPRINTS.includes(opt.value),
      );
    }
    return all;
  }, [target, t]);

  return (
    <AF.Col>
      <div className="rounded-lg border border-border bg-muted/30 p-4 mb-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          {t("decoupageType")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AF.SelectField
            name="type"
            className="w-full"
            label={t("typeDecoupageSelectLabel")}
            options={typeOptions}
            placeholder={selectTypePlaceholder}
          />
          <AF.SelectField
            name="sortByInsideDecoupage"
            className="w-full"
            label={t("sortByInsideDecoupageLabel")}
            options={sortOptions}
            placeholder={t("selectType")}
          />
        </div>
      </div>
      <Separator className="my-4" />
      <Sortable value={fields} onMove={handleMove} orientation="mixed">
        <div className="grid grid-cols-2 gap-9 w-full">
          {fields.map((field, index) => (
            <SortableItem key={field.id} value={field.id} className="min-w-0">
              <DecoupageCard
                index={index}
                isCollapsed={collapsedById[field.id] ?? false}
                onToggleCollapse={() => toggleCollapsed(field.id)}
                onRemove={() => remove(index)}
                form={form}
                target={target}
                cmsID={cmsID}
                update={update}
                showCategoryFields={showCategoryFields}
                selectedType={selectedType}
                categoriesData={categoriesData}
                isLoadingCategories={isLoadingCategories}
                categoryLabel={categoryLabel}
              />
            </SortableItem>
          ))}
        </div>
      </Sortable>
      <Separator className="my-4" />
      <Button
        type="button"
        variant="ghost"
        className="w-full bg-transparent hover:text-white"
        onClick={() =>
          // Les 4 locales sont REQUISES par catalogueRaisonneSectionUpsertSchema
          // (runtime) : n'en semer que 2 faisait échouer la validation au submit
          // d'un découpage fraîchement ajouté.
          append({
            nameMultilingual: { fr: "", en: "", es: "", nl: "" },
            titleMultilingual: { fr: "", en: "", es: "", nl: "" },
            contentMultilingual: { fr: "", en: "", es: "", nl: "" },
            showContent: false,
            categoryMultilingual: constructLocaleDict(() => ""),
            startValue: "",
            endValue: "",
            categoryId: "",
            order: fields.length + 1,
            vignette: null,
            type: selectedType ?? "creationDate",
          })
        }
      >
        {t("addDecoupage")}
      </Button>
    </AF.Col>
  );
};

export default DecoupageCatalogueRaisonneForm;
